export interface WildflowerDaySummary {
  dateStr: string;
  dayNum: number;
  stageCode: number;
  stageName: string;
  badgeLabel: string;
  colorHex: string;
  isToday: boolean;
  concurrencyPct: number;
}

export interface SpeciesTrendCurve {
  speciesId: string;
  name: string;
  color: string;
  isSuitable: boolean;
  peakDateStr: string;
  points: Array<{
    dateStr: string;
    dayOfYear: number;
    concurrencyPct: number;
    stageCode: number;
  }>;
}

export interface WildflowerPoiProfile {
  lat: number;
  lon: number;
  elevationMeters: number;
  snowmeltDoy?: number;
  peakMeadowDateStr: string;
  mbiiScore: number;
  speciesCurves: SpeciesTrendCurve[];
  // Desert specific
  biiScore?: number;
  autumnLeachingStatus?: string;
  stormCadenceDays?: number;
  // Berries specific
  qualityScore?: number;
  qualityBadge?: string;
  elevationBand?: string;
  soilMoistureStatus?: string;
}

export class WildflowerService {

  /**
   * Returns default calendar target date and month index for the given product.
   */
  public static getDefaultDateInfo(
    productKey: string = 'alpine_wildflowers',
    nowDate: Date = new Date()
  ): { dateStr: string; monthIndex: number } {
    const year = nowDate.getFullYear();

    if (productKey === 'desert_wildflowers') {
      const jan1 = new Date(year, 0, 1);
      const may31 = new Date(year, 4, 31);
      let targetDate = nowDate;
      let monthIndex = 2; // Default to March (peak desert superbloom)

      if (nowDate >= jan1 && nowDate <= may31) {
        targetDate = nowDate;
        monthIndex = nowDate.getMonth(); // 0=Jan, 1=Feb, 2=Mar, 3=Apr, 4=May
      } else {
        targetDate = new Date(year, 2, 15);
        monthIndex = 2;
      }

      const yyyy = targetDate.getFullYear();
      const mm = String(targetDate.getMonth() + 1).padStart(2, '0');
      const dd = String(targetDate.getDate()).padStart(2, '0');
      const dateStr = `${yyyy}-${mm}-${dd}`;
      return { dateStr, monthIndex: Math.max(0, Math.min(4, monthIndex)) };

    } else if (productKey === 'wild_berries') {
      const jun1 = new Date(year, 5, 1);
      const oct31 = new Date(year, 9, 31);
      let targetDate = nowDate;
      let monthIndex = 2; // Default to August (peak harvest window)

      if (nowDate >= jun1 && nowDate <= oct31) {
        targetDate = nowDate;
        monthIndex = nowDate.getMonth() - 5; // 5=Jun->0, 6=Jul->1, 7=Aug->2, 8=Sep->3, 9=Oct->4
      } else {
        targetDate = new Date(year, 7, 15);
        monthIndex = 2;
      }

      const yyyy = targetDate.getFullYear();
      const mm = String(targetDate.getMonth() + 1).padStart(2, '0');
      const dd = String(targetDate.getDate()).padStart(2, '0');
      const dateStr = `${yyyy}-${mm}-${dd}`;
      return { dateStr, monthIndex: Math.max(0, Math.min(4, monthIndex)) };

    } else {
      // Default: alpine_wildflowers (March to August)
      const mar1 = new Date(year, 2, 1);
      const aug31 = new Date(year, 7, 31);
      let targetDate = nowDate;
      let monthIndex = 4; // Default to July (peak alpine season: index 4)

      if (nowDate >= mar1 && nowDate <= aug31) {
        targetDate = nowDate;
        monthIndex = nowDate.getMonth() - 2; // 2=Mar->0, 3=Apr->1, 4=May->2, 5=Jun->3, 6=Jul->4, 7=Aug->5
      } else if (nowDate < mar1) {
        targetDate = mar1;
        monthIndex = 0;
      } else {
        targetDate = new Date(year, 6, 15);
        monthIndex = 4;
      }

      const yyyy = targetDate.getFullYear();
      const mm = String(targetDate.getMonth() + 1).padStart(2, '0');
      const dd = String(targetDate.getDate()).padStart(2, '0');
      const dateStr = `${yyyy}-${mm}-${dd}`;
      return { dateStr, monthIndex: Math.max(0, Math.min(5, monthIndex)) };
    }
  }

  /**
   * Returns upcoming Saturday and next Saturday date strings for weekend planning.
   */
  public static getWeekendDates(nowDate: Date = new Date()): { thisSaturday: string; nextSaturday: string } {
    const currentDay = nowDate.getDay(); // 0 = Sun, 6 = Sat
    const daysUntilThisSat = (6 - currentDay + 7) % 7;
    const thisSat = new Date(nowDate);
    thisSat.setDate(nowDate.getDate() + (daysUntilThisSat === 0 ? 0 : daysUntilThisSat));

    const nextSat = new Date(thisSat);
    nextSat.setDate(thisSat.getDate() + 7);

    const fmt = (d: Date) => {
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    };

    return {
      thisSaturday: fmt(thisSat),
      nextSaturday: fmt(nextSat)
    };
  }

  /**
   * Evaluates biophysical profile and multi-species trend curves for a location.
   */
  public static getPoiProfile(
    lat: number,
    lon: number,
    elevMeters: number = 1600,
    year: number = 2026,
    productKey: string = 'alpine_wildflowers'
  ): WildflowerPoiProfile {
    if (productKey === 'desert_wildflowers') {
      return this.getDesertPoiProfile(lat, lon, elevMeters, year);
    } else if (productKey === 'wild_berries') {
      return this.getBerryPoiProfile(lat, lon, elevMeters, year);
    }
    return this.getAlpinePoiProfile(lat, lon, elevMeters, year);
  }

  /**
   * Alpine & Mountain Perennial Wildflower profile.
   */
  private static getAlpinePoiProfile(
    lat: number,
    lon: number,
    elevMeters: number = 1600,
    year: number = 2026
  ): WildflowerPoiProfile {
    const latFactor = Math.max(0.0, Math.min(1.0, (lat - 32.0) / 28.0));
    let meltoutDoy: number;
    if (elevMeters <= 500.0) {
      meltoutDoy = 45.0 + latFactor * 15.0 + (elevMeters / 500.0) * 20.0;
    } else {
      meltoutDoy = (75.0 + latFactor * 20.0) + ((elevMeters - 500.0) / 100.0) * 5.2;
    }
    meltoutDoy = Math.max(45.0, Math.min(240.0, meltoutDoy));

    const tAnn = 18.0 - (lat - 25.0) * 0.38;
    const amp = 9.0 + (lat - 25.0) * 0.15;
    const lapse = (elevMeters / 1000.0) * 5.8;
    const omega = (2.0 * Math.PI) / 365.25;

    const speciesConfig = [
      {
        id: 'composite',
        name: 'Meadow & Foothill Wave (All Species)',
        color: '#e91e63',
        tBase: 4.0,
        tauPeak: 200.0,
        elevMin: 30,
        elevMax: 3400,
        latMin: 34.0,
        latMax: 62.0,
        windowWidth: 28
      },
      {
        id: 'woodland_trillium',
        name: 'Coast & Lowland Trillium (Early Forest)',
        color: '#80deea',
        tBase: 3.5,
        tauPeak: 95.0,
        elevMin: 30,
        elevMax: 1200,
        latMin: 39.0,
        latMax: 54.0,
        windowWidth: 20
      },
      {
        id: 'balsamroot',
        name: 'Arrowleaf Balsamroot (Gorge & Montane)',
        color: '#fbc02d',
        tBase: 4.0,
        tauPeak: 180.0,
        elevMin: 80,
        elevMax: 2400,
        latMin: 36.0,
        latMax: 53.0,
        windowWidth: 24
      },
      {
        id: 'rhododendron_coast',
        name: 'Pacific Rhododendron & Foxglove',
        color: '#f06292',
        tBase: 4.5,
        tauPeak: 210.0,
        elevMin: 30,
        elevMax: 1600,
        latMin: 39.5,
        latMax: 50.0,
        windowWidth: 24
      },
      {
        id: 'lupine_paintbrush',
        name: 'Subalpine Lupine & Paintbrush',
        color: '#ab47bc',
        tBase: 4.0,
        tauPeak: 240.0,
        elevMin: 400,
        elevMax: 3400,
        latMin: 34.0,
        latMax: 60.0,
        windowWidth: 24
      },
      {
        id: 'avalanche_lily',
        name: 'Avalanche Lily (Snow Margins)',
        color: '#fff59d',
        tBase: 2.0,
        tauPeak: 110.0,
        elevMin: 1100,
        elevMax: 2600,
        latMin: 43.5,
        latMax: 54.0,
        windowWidth: 18
      }
    ];

    const curves: SpeciesTrendCurve[] = [];
    let peakMeadowDateStr = '';

    for (const sp of speciesConfig) {
      const isSuitable =
        elevMeters >= sp.elevMin &&
        elevMeters <= sp.elevMax &&
        lat >= sp.latMin &&
        lat <= sp.latMax;

      let peakDoy = 245;
      for (let d = Math.floor(meltoutDoy); d <= 245; d++) {
        const days = Math.max(1.0, d - meltoutDoy);
        const intSin = (Math.sin(omega * (d - 200.0)) - Math.sin(omega * (meltoutDoy - 200.0))) / omega;
        const meanT = (tAnn - lapse) + amp * (intSin / days);
        const agdd = Math.max(0.0, meanT - sp.tBase) * days;
        if (agdd >= sp.tauPeak) {
          peakDoy = d;
          break;
        }
      }

      const peakDate = new Date(year, 0, 1);
      peakDate.setDate(Math.round(peakDoy));
      const peakYyyy = peakDate.getFullYear();
      const peakMm = String(peakDate.getMonth() + 1).padStart(2, '0');
      const peakDd = String(peakDate.getDate()).padStart(2, '0');
      const spPeakDateStr = `${peakYyyy}-${peakMm}-${peakDd}`;

      if (sp.id === 'composite') {
        peakMeadowDateStr = spPeakDateStr;
      }

      // March 1 (DOY 60) to Aug 31 (DOY 243) = 184 days
      const points = [];
      const mar1 = new Date(year, 2, 1);
      for (let dayOffset = 0; dayOffset < 184; dayOffset++) {
        const d = new Date(mar1);
        d.setDate(mar1.getDate() + dayOffset);
        const curDoy = Math.floor((d.getTime() - new Date(year, 0, 1).getTime()) / (86400 * 1000)) + 1;

        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        const dateStr = `${yyyy}-${mm}-${dd}`;

        let concurrency = 0;
        let stageCode = 5;

        if (curDoy < meltoutDoy) {
          stageCode = 0; // Snowbound
          concurrency = 0;
        } else {
          const sigma = sp.windowWidth / 2.355;
          const deltaDoy = curDoy - peakDoy;
          const bell = Math.exp(-(deltaDoy * deltaDoy) / (2.0 * sigma * sigma));
          concurrency = Math.round(bell * 100);

          if (deltaDoy < 0) {
            if (concurrency < 10) {
              stageCode = 1; // Emerging / Vegetative
            } else if (concurrency < 50) {
              stageCode = 2; // Early Bloom / Budding
            } else {
              stageCode = 3; // Peak Floral Display
            }
          } else {
            if (concurrency >= 50) {
              stageCode = 3; // Peak Floral Display
            } else if (concurrency >= 15) {
              stageCode = 4; // Past Peak / Fading
            } else {
              stageCode = 5; // Bloom Ended
            }
          }
        }

        points.push({
          dateStr,
          dayOfYear: curDoy,
          concurrencyPct: isSuitable ? concurrency : 0,
          stageCode: isSuitable ? stageCode : 5
        });
      }

      curves.push({
        speciesId: sp.id,
        name: sp.name,
        color: sp.color,
        isSuitable,
        peakDateStr: spPeakDateStr,
        points
      });
    }

    const syncFactor = Math.max(0.35, Math.min(1.0, 0.35 + ((meltoutDoy - 120.0) / 60.0) * 0.55));
    const insulation = Math.max(0.5, Math.min(1.0, 0.5 + (elevMeters / 2500.0) * 0.5));
    const moisture = Math.max(0.6, Math.min(1.0, 0.6 + (elevMeters / 3000.0) * 0.4));
    const frostPenalty = Math.max(0.0, Math.min(0.4, (elevMeters - 2200.0) / 1500.0 * 0.3));
    const mbii = Math.round(100.0 * (syncFactor * insulation * moisture) * (1.0 - frostPenalty));

    return {
      lat,
      lon,
      elevationMeters: elevMeters,
      snowmeltDoy: Math.round(meltoutDoy),
      peakMeadowDateStr,
      mbiiScore: mbii,
      speciesCurves: curves
    };
  }

  /**
   * Desert Superbloom & Annual Wildflower profile.
   */
  private static getDesertPoiProfile(
    lat: number,
    lon: number,
    elevMeters: number = 200,
    year: number = 2026
  ): WildflowerPoiProfile {
    // Desert basins: biofix initiates from autumn rain leaching and Jan 1 thermal forcing
    const tAnn = 22.0 - (lat - 32.0) * 0.45;
    const amp = 11.0 + (lat - 32.0) * 0.12;
    const lapse = (Math.max(0.0, elevMeters) / 1000.0) * 6.2;
    const omega = (2.0 * Math.PI) / 365.25;

    const speciesConfig = [
      {
        id: 'composite',
        name: 'Broad Basin Superbloom (All Annuals)',
        color: '#e91e63',
        tBase: 10.0,
        tauPeak: 260.0,
        elevMin: -90,
        elevMax: 1600,
        latMin: 31.0,
        latMax: 38.0,
        windowWidth: 26
      },
      {
        id: 'desert_gold',
        name: 'Desert Gold / Sunflower (Death Valley)',
        color: '#ffd54f',
        tBase: 10.0,
        tauPeak: 220.0,
        elevMin: -90,
        elevMax: 1000,
        latMin: 32.0,
        latMax: 37.0,
        windowWidth: 22
      },
      {
        id: 'ca_poppy',
        name: 'California Poppy (Antelope Valley)',
        color: '#ff9800',
        tBase: 9.0,
        tauPeak: 310.0,
        elevMin: 300,
        elevMax: 1400,
        latMin: 33.0,
        latMax: 39.0,
        windowWidth: 28
      },
      {
        id: 'desert_dandelion',
        name: 'Desert Dandelion (Anza-Borrego)',
        color: '#fff59d',
        tBase: 10.0,
        tauPeak: 240.0,
        elevMin: -50,
        elevMax: 1200,
        latMin: 32.0,
        latMax: 36.5,
        windowWidth: 22
      },
      {
        id: 'phacelia',
        name: 'Notch-leaf Phacelia (Desert Washes)',
        color: '#ab47bc',
        tBase: 9.5,
        tauPeak: 250.0,
        elevMin: -90,
        elevMax: 1300,
        latMin: 32.0,
        latMax: 37.5,
        windowWidth: 24
      },
      {
        id: 'desert_lupine',
        name: 'Desert / Arizona Lupine (Sonoran)',
        color: '#7e57c2',
        tBase: 9.0,
        tauPeak: 270.0,
        elevMin: 150,
        elevMax: 1500,
        latMin: 31.5,
        latMax: 35.5,
        windowWidth: 24
      }
    ];

    const curves: SpeciesTrendCurve[] = [];
    let peakMeadowDateStr = '';

    for (const sp of speciesConfig) {
      const isSuitable =
        elevMeters >= sp.elevMin &&
        elevMeters <= sp.elevMax &&
        lat >= sp.latMin &&
        lat <= sp.latMax;

      // Thermal accumulation from January 1 (DOY 1)
      let peakDoy = 75; // Baseline mid-March
      for (let d = 1; d <= 151; d++) {
        const intSin = (Math.sin(omega * (d - 200.0)) - Math.sin(omega * (1.0 - 200.0))) / omega;
        const meanT = (tAnn - lapse) + amp * (intSin / d);
        const agdd = Math.max(0.0, meanT - sp.tBase) * d;
        if (agdd >= sp.tauPeak) {
          peakDoy = d;
          break;
        }
      }

      const peakDate = new Date(year, 0, 1);
      peakDate.setDate(Math.round(peakDoy));
      const peakYyyy = peakDate.getFullYear();
      const peakMm = String(peakDate.getMonth() + 1).padStart(2, '0');
      const peakDd = String(peakDate.getDate()).padStart(2, '0');
      const spPeakDateStr = `${peakYyyy}-${peakMm}-${peakDd}`;

      if (sp.id === 'composite') {
        peakMeadowDateStr = spPeakDateStr;
      }

      // January 1 (DOY 1) to May 31 (DOY 151) = 151 days
      const points = [];
      const jan1 = new Date(year, 0, 1);
      for (let dayOffset = 0; dayOffset < 151; dayOffset++) {
        const d = new Date(jan1);
        d.setDate(jan1.getDate() + dayOffset);
        const curDoy = dayOffset + 1;

        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        const dateStr = `${yyyy}-${mm}-${dd}`;

        const sigma = sp.windowWidth / 2.355;
        const deltaDoy = curDoy - peakDoy;
        const bell = Math.exp(-(deltaDoy * deltaDoy) / (2.0 * sigma * sigma));
        const concurrency = Math.round(bell * 100);

        let stageCode = 0;
        if (curDoy < peakDoy - sp.windowWidth) {
          stageCode = 0; // Dormant Seed Bank
        } else if (deltaDoy < -10) {
          stageCode = 1; // Germination / Rosette
        } else if (deltaDoy < 0) {
          stageCode = 2; // Early Blooms / Budding
        } else if (deltaDoy <= 8) {
          stageCode = 3; // Peak Floral Carpet
        } else if (deltaDoy <= 22) {
          stageCode = 4; // Past Peak / Desiccation
        } else {
          stageCode = 5; // Summer Quiescence
        }

        points.push({
          dateStr,
          dayOfYear: curDoy,
          concurrencyPct: isSuitable ? concurrency : 0,
          stageCode: isSuitable ? stageCode : 0
        });
      }

      curves.push({
        speciesId: sp.id,
        name: sp.name,
        color: sp.color,
        isSuitable,
        peakDateStr: spPeakDateStr,
        points
      });
    }

    // BII calculation (0-100)
    const anomalyFactor = 1.35;
    const washTrigger = 1.0; // Primed
    const cadenceFactor = 0.88;
    const stressFactor = 0.95;
    const bii = Math.round(100.0 * Math.min(1.0, washTrigger * cadenceFactor * (anomalyFactor / 2.0) * stressFactor));

    return {
      lat,
      lon,
      elevationMeters: elevMeters,
      peakMeadowDateStr,
      mbiiScore: bii,
      biiScore: bii,
      autumnLeachingStatus: 'Primed (≥20mm soaking rain)',
      stormCadenceDays: 12,
      speciesCurves: curves
    };
  }

  /**
   * Wild Berry Ripening & Foraging profile.
   */
  private static getBerryPoiProfile(
    lat: number,
    lon: number,
    elevMeters: number = 800,
    year: number = 2026
  ): WildflowerPoiProfile {
    const tAnn = 14.0 - (lat - 38.0) * 0.42;
    const amp = 8.5 + (lat - 38.0) * 0.12;
    const lapse = (elevMeters / 1000.0) * 5.6;
    const omega = (2.0 * Math.PI) / 365.25;

    const speciesConfig = [
      {
        id: 'composite',
        name: 'Composite Ripening Wave (All Species)',
        color: '#9c27b0',
        tBase: 5.5,
        tauPeak: 680.0,
        elevMin: 50,
        elevMax: 2400,
        latMin: 40.0,
        latMax: 58.0,
        windowWidth: 32
      },
      {
        id: 'serviceberry',
        name: 'Serviceberry / Juneberry (Midwest & Plains)',
        color: '#ffb74d',
        tBase: 5.0,
        tauPeak: 440.0,
        elevMin: 50,
        elevMax: 2400,
        latMin: 38.0,
        latMax: 56.0,
        windowWidth: 24
      },
      {
        id: 'salmonberry',
        name: 'Salmonberry (Coastal PNW)',
        color: '#e91e63',
        tBase: 6.0,
        tauPeak: 450.0,
        elevMin: 20,
        elevMax: 1000,
        latMin: 42.0,
        latMax: 56.0,
        windowWidth: 24
      },
      {
        id: 'thimbleberry',
        name: 'Thimbleberry (Montane Canyons)',
        color: '#f06292',
        tBase: 6.0,
        tauPeak: 580.0,
        elevMin: 100,
        elevMax: 2200,
        latMin: 36.0,
        latMax: 54.0,
        windowWidth: 26
      },
      {
        id: 'blueberry',
        name: 'Lowbush Wild Blueberry (Boreal / Northeast)',
        color: '#42a5f5',
        tBase: 6.0,
        tauPeak: 620.0,
        elevMin: 10,
        elevMax: 1400,
        latMin: 41.0,
        latMax: 52.0,
        windowWidth: 26
      },
      {
        id: 'huckleberry',
        name: 'Mountain Huckleberry (Subalpine)',
        color: '#5c6bc0',
        tBase: 5.0,
        tauPeak: 760.0,
        elevMin: 600,
        elevMax: 2300,
        latMin: 41.5,
        latMax: 55.0,
        windowWidth: 28
      },
      {
        id: 'blackberry',
        name: 'Wild Blackberry & Dewberry (Continental)',
        color: '#263238',
        tBase: 9.0,
        tauPeak: 820.0,
        elevMin: 0,
        elevMax: 1600,
        latMin: 28.0,
        latMax: 48.0,
        windowWidth: 30
      },
      {
        id: 'elderberry',
        name: 'American Black Elderberry (Eastern / Central)',
        color: '#8e24aa',
        tBase: 8.0,
        tauPeak: 900.0,
        elevMin: 0,
        elevMax: 1200,
        latMin: 28.0,
        latMax: 48.0,
        windowWidth: 28
      }
    ];

    const curves: SpeciesTrendCurve[] = [];
    let peakMeadowDateStr = '';

    for (const sp of speciesConfig) {
      const isSuitable =
        elevMeters >= sp.elevMin &&
        elevMeters <= sp.elevMax &&
        lat >= sp.latMin &&
        lat <= sp.latMax;

      // Two-phase thermal accumulation to peak sugar harvest
      let peakDoy = 230; // Mid-August
      for (let d = 80; d <= 285; d++) {
        const days = Math.max(1.0, d - 80);
        const intSin = (Math.sin(omega * (d - 200.0)) - Math.sin(omega * (80.0 - 200.0))) / omega;
        const meanT = (tAnn - lapse) + amp * (intSin / days);
        const agdd = Math.max(0.0, meanT - sp.tBase) * days;
        if (agdd >= sp.tauPeak) {
          peakDoy = d;
          break;
        }
      }

      const peakDate = new Date(year, 0, 1);
      peakDate.setDate(Math.round(peakDoy));
      const peakYyyy = peakDate.getFullYear();
      const peakMm = String(peakDate.getMonth() + 1).padStart(2, '0');
      const peakDd = String(peakDate.getDate()).padStart(2, '0');
      const spPeakDateStr = `${peakYyyy}-${peakMm}-${peakDd}`;

      if (sp.id === 'composite') {
        peakMeadowDateStr = spPeakDateStr;
      }

      // June 1 (DOY 152) to October 31 (DOY 304) = 153 days
      const points = [];
      const jun1 = new Date(year, 5, 1);
      for (let dayOffset = 0; dayOffset < 153; dayOffset++) {
        const d = new Date(jun1);
        d.setDate(jun1.getDate() + dayOffset);
        const curDoy = 152 + dayOffset;

        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        const dateStr = `${yyyy}-${mm}-${dd}`;

        const sigma = sp.windowWidth / 2.355;
        const deltaDoy = curDoy - peakDoy;
        const bell = Math.exp(-(deltaDoy * deltaDoy) / (2.0 * sigma * sigma));
        const concurrency = Math.round(bell * 100);

        let stageCode = 0;
        if (deltaDoy < -24) {
          stageCode = 1; // Flowering Anthesis
        } else if (deltaDoy < -10) {
          stageCode = 2; // Green Fruit Swelling
        } else if (deltaDoy < 0) {
          stageCode = 3; // Early Ripe Window
        } else if (deltaDoy <= 12) {
          stageCode = 4; // Peak Sugar Harvest
        } else {
          stageCode = 5; // Overripe / Dropped
        }

        points.push({
          dateStr,
          dayOfYear: curDoy,
          concurrencyPct: isSuitable ? concurrency : 0,
          stageCode: isSuitable ? stageCode : 0
        });
      }

      curves.push({
        speciesId: sp.id,
        name: sp.name,
        color: sp.color,
        isSuitable,
        peakDateStr: spPeakDateStr,
        points
      });
    }

    const elevFt = Math.round(elevMeters * 3.28084);
    const bandLower = Math.floor(elevFt / 1000) * 1000;
    const bandUpper = bandLower + 1500;
    const qualityScore = 88;

    return {
      lat,
      lon,
      elevationMeters: elevMeters,
      peakMeadowDateStr,
      mbiiScore: qualityScore,
      qualityScore,
      qualityBadge: 'Prime / Plump (Adequate Root Moisture)',
      elevationBand: `${bandLower.toLocaleString()} - ${bandUpper.toLocaleString()} ft`,
      soilMoistureStatus: 'Normal / Ample (+0.4σ)',
      speciesCurves: curves
    };
  }

  /**
   * Generates calendar grid cells for a given month index.
   */
  public static getMonthCalendar(
    monthIndex: number,
    lat: number,
    lon: number,
    elevMeters: number = 1600,
    speciesId: string = 'composite',
    year: number = 2026,
    productKey: string = 'alpine_wildflowers'
  ): { days: WildflowerDaySummary[]; profile: WildflowerPoiProfile; firstDayCol: number; monthName: string } {
    const profile = this.getPoiProfile(lat, lon, elevMeters, year, productKey);
    const targetCurve = profile.speciesCurves.find(c => c.speciesId === speciesId) || profile.speciesCurves[0];

    let monthNames: string[];
    let targetMonth: number;
    let stageColors: string[];
    let stageNames: string[];
    let badgeLabels: string[];

    if (productKey === 'desert_wildflowers') {
      monthNames = ['January', 'February', 'March', 'April', 'May'];
      targetMonth = Math.max(0, Math.min(4, monthIndex));
      stageColors = [
        '#64748b', // 0: Dormant Seed Bank
        '#81c784', // 1: Germination / Rosette
        '#cddc39', // 2: Early Blooms / Budding
        '#e91e63', // 3: Peak Floral Carpet
        '#ff9800', // 4: Past Peak / Desiccation
        '#90a4ae'  // 5: Summer Quiescence
      ];
      stageNames = [
        'Dormant Seed Bank',
        'Germination / Rosette',
        'Early Blooms / Budding',
        'Peak Floral Carpet',
        'Past Peak / Desiccation',
        'Summer Quiescence'
      ];
      badgeLabels = ['Seed', 'Rosette', 'Early', 'Peak', 'Fading', 'Dormant'];

    } else if (productKey === 'wild_berries') {
      monthNames = ['June', 'July', 'August', 'September', 'October'];
      targetMonth = Math.max(0, Math.min(4, monthIndex)) + 5; // 5=Jun to 9=Oct
      stageColors = [
        '#64748b', // 0: Dormant / Pre-Anthesis
        '#cddc39', // 1: Flowering Anthesis
        '#81c784', // 2: Green Fruit Swelling
        '#ba68c8', // 3: Early Ripe Window
        '#7b1fa2', // 4: Peak Sugar Harvest
        '#8d6e63'  // 5: Overripe / Dropped
      ];
      stageNames = [
        'Dormant / Pre-Anthesis',
        'Flowering Anthesis',
        'Green Fruit Swelling',
        'Early Ripe Window',
        'Peak Sugar Harvest',
        'Overripe / Dropped'
      ];
      badgeLabels = ['Dormant', 'Flower', 'Green', 'Early', 'Peak', 'Drop'];

    } else {
      // alpine_wildflowers
      monthNames = ['March', 'April', 'May', 'June', 'July', 'August'];
      targetMonth = Math.max(0, Math.min(5, monthIndex)) + 2; // 2=Mar to 7=Aug
      stageColors = [
        '#a0d2f5', // 0: Snowbound
        '#81c784', // 1: Vegetative
        '#cddc39', // 2: Early Bloom
        '#e91e63', // 3: Peak
        '#ff9800', // 4: Past Peak
        '#64748b'  // 5: Bloom Ended
      ];
      stageNames = [
        'Snowbound / Pre-Melt',
        'Emerging / Vegetative',
        'Early Bloom / Budding',
        'Peak Floral Display',
        'Past Peak / Fading',
        'Bloom Ended / Done'
      ];
      badgeLabels = ['Snow', 'Green', 'Buds', 'Peak', 'Past', 'Ended'];
    }

    const monthName = monthNames[Math.max(0, Math.min(monthNames.length - 1, monthIndex))] || 'Month';

    const firstDate = new Date(year, targetMonth, 1);
    const firstDayCol = firstDate.getDay();

    const daysInMonth = new Date(year, targetMonth + 1, 0).getDate();
    const today = new Date();
    const todayYyyy = today.getFullYear();
    const todayMm = String(today.getMonth() + 1).padStart(2, '0');
    const todayDd = String(today.getDate()).padStart(2, '0');
    const todayStr = `${todayYyyy}-${todayMm}-${todayDd}`;

    const days: WildflowerDaySummary[] = [];

    for (let dayNum = 1; dayNum <= daysInMonth; dayNum++) {
      const mm = String(targetMonth + 1).padStart(2, '0');
      const dd = String(dayNum).padStart(2, '0');
      const dateStr = `${year}-${mm}-${dd}`;

      const matchedPoint = targetCurve ? targetCurve.points.find(p => p.dateStr === dateStr) : null;
      const stageCode = matchedPoint ? matchedPoint.stageCode : 0;
      const concurrency = matchedPoint ? matchedPoint.concurrencyPct : 0;

      days.push({
        dateStr,
        dayNum,
        stageCode,
        stageName: stageNames[stageCode] || 'Unknown',
        badgeLabel: badgeLabels[stageCode] || '',
        colorHex: stageColors[stageCode] || '#64748b',
        isToday: dateStr === todayStr,
        concurrencyPct: concurrency
      });
    }

    return {
      days,
      profile,
      firstDayCol,
      monthName
    };
  }
}
